import { useContext, useEffect, useState, MutableRefObject, ReactNode } from "react"
import { TabListStateContext as RATabListStateContext } from "react-aria-components"
import { twJoin } from "tailwind-merge"

export function SelectedTabIndicator({
  tabListRef: tabListEl,
  children,
}: {
  tabListRef: MutableRefObject<HTMLDivElement | null>
  children: ReactNode
}) {
  const [selectedTabRect, setSelectedTabRect] = useState<
    { left: number; width: number } | undefined
  >(undefined)
  const [hasMeasured, setHasMeasured] = useState(false)
  const tabListState = useContext(RATabListStateContext)
  const isFirstTabSelected =
    tabListState?.collection.getFirstKey() === tabListState?.selectedKey?.toString()
  const selectedTabEl: HTMLDivElement | null =
    tabListEl.current && tabListState?.selectedKey
      ? tabListEl.current.querySelector(
          `[data-key="${CSS.escape(tabListState.selectedKey.toString())}"]`,
        )
      : null

  useEffect(() => {
    if (!tabListEl.current) {
      return
    }

    if (!selectedTabEl) {
      return
    }

    setSelectedTabRect({
      left: selectedTabEl.offsetLeft, // Relative to RATabs component
      width: selectedTabEl.offsetWidth,
    })

    // Need this to be set in the next tick so that a rerender is triggered only when the rect's measurement is properly registered by React
    const id = requestAnimationFrame(() => setHasMeasured(true))

    return () => {
      if (id) {
        cancelAnimationFrame(id)
      }
    }
  }, [tabListEl, tabListState?.selectedKey, selectedTabEl])

  return (
    <div className="relative">
      {children}
      <div
        className={twJoin(
          "absolute h-0.5 -translate-y-1 bg-primary-base bg-clip-content transition-[left,width] motion-reduce:transition-none",
          hasMeasured ? "duration-20" : "duration-0", // Only animate when the measurements are ready
          isFirstTabSelected ? "pr-2" : "px-2",
        )}
        style={
          selectedTabRect
            ? {
                left: selectedTabRect.left,
                width: selectedTabRect.width,
              }
            : undefined
        }
      />
    </div>
  )
}
